/* eslint-disable react/prop-types */
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import { connect } from 'react-redux';

import { fetchGroup } from '@/actions/group';
import { notify } from '@/actions/ui';
import Breadcrumbs from '@/components/Breadcrumbs';
import Button from '@/components/Button';
import CircularProgress from '@/components/CircularProgress/CircularProgress';
import CountBox from '@/components/CountBox';
import Divider from '@/components/Divider';
import GroupMember from '@/components/GroupMember';
import FAIcon from '@/components/Icon/FAIcon';
import Link from '@/components/Link';
import MemberRequest from '@/components/MemberRequests/Group';
import Pagination from '@/components/Pagination/Pagination';
import Tooltip from '@/componentsv2/Tooltip';
import { money } from '@/core/money';
import { Viewer } from '@/core/viewer';
import { primary } from '@/theme/colors';
import { formatCredits } from '@/utils';

import { ActivityGuardModal } from './ActivityGuardModal';
import AddTeamMemberDialog from './AddTeamMemberDialog';
import TeamSMB from './TeamSMB';
import { useTeamMembers } from './useTeamMembers';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  headerLink: {
    whiteSpace: 'nowrap',
    marginLeft: 15,
    fontSize: 14,
  },
  members: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  membersActions: {
    width: 200,
  },
  membersActionsTitle: {
    color: 'var(--darkGray)',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  membersList: {
    flex: 1,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 20,
    borderTop: '1px solid var(--borderColor)',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 25,
    '& span': {
      paddingTop: 15,
    },
  },
  counts: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 24,
    // @ts-expect-error TS(2339) FIXME: Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      gap: 16,
      '& > *': {
        width: 170,
      },
    },
  },
  search: {
    marginLeft: 'auto',
    // @ts-expect-error TS(2339) FIXME: Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      marginTop: 10,
      width: 190,
    },
  },
}));

function TeamDefault({ viewer, groupId, team, notify, fetchGroup }: any) {
  const s = useStyles();
  const { name, stats = {}, html_url: htmlUrl } = team;

  const hook = useTeamMembers({ viewer, groupId, team, notify, fetchGroup });

  const creditBalance = hook.billingAccount
    ? money(hook.billingAccount.credit_balance)
    : money({ cents: 0, currency: 'OFC' });

  const handleAddClose = () => hook.setAddOpen(false);

  return (
    // @ts-expect-error TS(2339) FIXME: Property 'root' does not exist on type 'ClassNameM... Remove this comment to see the full error message
    <div className={s.root}>
      <div className={s.header}>
        <Breadcrumbs crumbs={[{ title: name, href: htmlUrl }]} />
        {team.internal_network && (
          <Link className={s.headerLink} to={`/search?networks[]=${team.internal_network.id}`}>
            <FAIcon icon="users-class" /> View Network
          </Link>
        )}
        <Link className={s.headerLink} to={`${htmlUrl}/settings`}>
          <FAIcon icon="cog" /> Settings &amp; Billing
        </Link>
        {viewer.groups?.length > 1 && (
          <Link className={s.headerLink} to="/teams">
            <FAIcon icon="exchange" /> View All Teams
          </Link>
        )}
      </div>

      {hook.awaitingMembersData && (
        <MemberRequest
          onApprove={hook.fetchPageMembers}
          memberRequests={hook.awaitingMembersData}
        />
      )}

      <div className={s.counts}>
        <CountBox count={hook.totalMembers} label="Team Members" />
        <CountBox count={stats.expert_request_count} label="Expert Requests" />
        <CountBox count={stats.consultation_count} label="Consultations" />
        <CountBox
          count={formatCredits(creditBalance.cents)}
          warn={creditBalance.cents <= 0}
          linkTo={hook.billingAccount && `${htmlUrl}/settings/credits`}
          label="Credits"
        />
        <div className={s.search}>
          <TextField
            value={hook.nameSearch ?? ''}
            margin="none"
            fullWidth={false}
            onChange={({ target: { value } }) => {
              hook.refetch({});
              hook.setPage(1);
              hook.setNameSearch(hook.normalizeSpace(value));
            }}
            placeholder="Filter by name"
          />
        </div>
      </div>
      <Divider />

      <div className={s.members}>
        <div className={s.membersActions}>
          <div className={s.membersActionsTitle}>Team Members</div>
          {!hook.perms.add ? (
            <Tooltip title="You do not have permission to add members.">
              <span>
                <Button color="white" variant="text" fontColor={primary} size="small" disabled>
                  Add
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              onClick={() => hook.setAddOpen(true)}
              color="white"
              variant="text"
              fontColor={primary}
              size="small"
            >
              Add
            </Button>
          )}
          <AddTeamMemberDialog
            groupId={groupId}
            userFilter={hook.addUserFilter}
            open={hook.addOpen}
            onSubmit={() => {
              hook.setAddTeamMemberLoading(true);
              handleAddClose();
            }}
            onClose={handleAddClose}
            onRequestDone={() => {
              hook.setAddTeamMemberLoading(false);
              hook.fetchPageMembers();
              hook.refreshSeatAllocation();
            }}
            addTeamMember={hook.addTeamMember}
            inviteUser={hook.inviteUser}
            isSMB={false}
          />
        </div>

        <div className={s.membersList}>
          {hook.isLoading ? (
            <div className={s.loading}>
              <CircularProgress size={52} />
              <span>Loading...</span>
            </div>
          ) : (
            hook.members.edges.map(({ node: m }: { node: any }) => (
              <GroupMember
                key={m.id}
                member={m}
                groupId={groupId}
                removeGroupMember={hook.canRemoveMember(m) ? hook.removeTeamMemberAndFetch : null}
                updateGroupMember={
                  hook.perms.update[m.role as keyof typeof hook.perms.update]
                    ? hook.updateTeamMemberAndFetch
                    : null
                }
                allowedRoles={hook.perms.allowedRoles.filter(
                  (r: string) =>
                    (m.user || r !== 'owner') &&
                    // Viewer is an SMB-only role on credit/enterprise accounts.
                    // Keep it in options only when the row already has it (so
                    // pre-existing viewer members still render their current
                    // role) but never let a new role be set to viewer.
                    (r !== 'viewer' || m.role === 'viewer')
                )}
                disabledRoles={[]}
              />
            ))
          )}
          {hook.totalPages > 1 && (
            <div className={s.pagination}>
              <Pagination
                page={hook.page}
                count={hook.totalPages}
                onChange={(_: any, value: any) => {
                  hook.refetch({ page: value });
                  hook.setPage(value);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <ActivityGuardModal
        open={!!hook.activityGuardError}
        onClose={() => hook.setActivityGuardError(null)}
        error={hook.activityGuardError}
      />
      {/* TransferOwnershipDialog is SMB-only and lives in TeamSMB; the
          non-SMB ownership-transfer path runs directly via the hook. */}
    </div>
  );
}

function Team({ viewer, groupId, team, notify, fetchGroup }: any) {
  const isSMB = team.billing_account?.isSMBAccount;
  if (isSMB) {
    return (
      <TeamSMB
        viewer={viewer}
        groupId={groupId}
        team={team}
        notify={notify}
        fetchGroup={fetchGroup}
      />
    );
  }
  return (
    <TeamDefault
      viewer={viewer}
      groupId={groupId}
      team={team}
      notify={notify}
      fetchGroup={fetchGroup}
    />
  );
}

const mapStateToProps = (
  state: { viewer: Viewer; groups: any },
  ownProps: { groupId: string }
) => ({
  viewer: state.viewer,
  groupId: ownProps.groupId,
  team: (state.groups.all.edges.find((g: any) => g.node.id === ownProps.groupId) || {}).node,
});

export default connect(mapStateToProps, {
  notify,
  fetchGroup,
})(Team);
